// Popover — a trigger-anchored panel of MIXED content on the React Aria
// foundation (ADR-029), per COMPONENTS §Overlay patterns. The complement to
// Menu: where Menu is a uniform list of commands/choices you arrow through,
// Popover is a small card holding arbitrary controls (a header, a segmented
// control, a radio group, links, actions) that you Tab between like a mini-form.
//
// Use it for the account panel, future filter panels, and rich settings cards.
// For a simple "pick one / run a command" dropdown use <Menu> instead.
//
// React Aria owns positioning (collision-aware flip/shift), focus management,
// Escape, click-outside, and RTL anchoring. We own 100% of the markup + tokens.
// The trigger is any focusable element (our IconButton / button) — wrapped in
// React Aria's <Pressable> so it drives open/close + aria-haspopup/expanded.
//
// TWO FACES, one trigger (ADR-080 §12): on a desk the anchored card; on a phone
// (the `md` tier) the family's `Sheet` from the bottom with the panel's name as
// its heading — the sheet reads the trigger's state, so every popover moves at
// once. A consumer's `panelClassName` dresses the DESK card only (its widths are
// the card's); on the sheet the inside fills the width. No height cap: the
// foundation sizes the card to the space it has.
//
// A FOOTER is a slot of its own, PINNED: the panel's content scrolls above it
// and never under it (a floating bar laid over a scroller lets the rows show
// through wherever the scroller has padding of its own — the phone sheet's).
// On a desk it closes the card; on a phone it is the sheet's footer.

import { useContext, type ComponentProps, type ReactElement, type ReactNode } from 'react';
import {
  DialogTrigger,
  Popover as AriaPopover,
  Dialog,
  OverlayTriggerStateContext,
  Pressable,
} from 'react-aria-components';
import type { PopoverProps as AriaPopoverProps } from 'react-aria-components';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { PHONE_QUERY, Sheet } from './Sheet.js';

export type PopoverPlacement = NonNullable<AriaPopoverProps['placement']>;

// The element type Pressable accepts — derived from the component so we don't
// import @react-types/shared directly.
type PressableChild = ComponentProps<typeof Pressable>['children'];

export interface PopoverProps {
  /** Accessible name for the panel (WCAG 4.1.2). */
  label: string;
  /** The focusable trigger (IconButton / button) — forwards ref + spreads props. */
  renderTrigger: () => ReactElement;
  /** Panel placement relative to the trigger; logical edges flip under RTL. Default 'bottom end'. */
  placement?: PopoverPlacement;
  /** Panel contents. The function form receives `close` (close-on-action) and the FACE it is on — `desk` · `phone` —
   *  so a host sizes its rows and its width by it (a desk card's width is `panelClassName`'s; the sheet fills). */
  children: ReactNode | ((close: () => void, face: 'desk' | 'phone') => ReactNode);
  /** A footer PINNED under the content — the panel's one standing action ("Reset view"); it never scrolls. The
   *  function form receives `close`. */
  footer?: ReactNode | ((close: () => void) => ReactNode);
  /** Extra classes merged onto the DESK card (a width, a padding). */
  panelClassName?: string;
  /** Forces the face (stories; the gate runs at one viewport). Default: the `md` tier. */
  face?: 'desk' | 'phone';
}

export function Popover({
  label,
  renderTrigger,
  placement = 'bottom end',
  children,
  footer,
  panelClassName = '',
  face,
}: PopoverProps) {
  const narrow = useMediaQuery(PHONE_QUERY);
  const phone = face ? face === 'phone' : narrow;
  const inside = (close: () => void) => (typeof children === 'function' ? children(close, phone ? 'phone' : 'desk') : children);
  const foot = (close: () => void) => (typeof footer === 'function' ? footer(close) : footer);
  const footed = footer !== undefined && footer !== null && footer !== false;
  return (
    <DialogTrigger>
      <Pressable>{renderTrigger() as PressableChild}</Pressable>
      {phone ? (
        <Sheet title={label} face="bottom" footer={footed ? <SheetFooter render={foot} /> : undefined}>
          {({ close }) => inside(close)}
        </Sheet>
      ) : (
        <AriaPopover
          placement={placement}
          offset={8}
          className={
            'z-dropdown min-w-[14rem] max-w-[20rem] rounded-md border border-[var(--color-border)] ' +
            'bg-[var(--color-bg-overlay)] shadow-overlay ' +
            // With a footer the CARD is a column: its content scrolls, the footer holds the bottom edge.
            (footed ? 'flex flex-col overflow-hidden ' : 'overflow-y-auto ') +
            'data-[entering]:o-pop-in data-[exiting]:o-pop-out ' +
            panelClassName
          }
        >
          <Dialog aria-label={label} className={footed ? 'flex min-h-0 flex-1 flex-col outline-none' : 'outline-none'}>
            {({ close }) => (
              // ONE shape, footed or not: a footer that comes or goes while the panel is open (a reset that shows
              // once something is hidden) leaves the content mounted — the keyboard's place is kept.
              <>
                <div className={footed ? 'min-h-0 flex-1 overflow-y-auto' : 'contents'}>{inside(close)}</div>
                {footed ? <div className="shrink-0 border-t border-[var(--color-divider)] px-2 py-1.5">{foot(close)}</div> : null}
              </>
            )}
          </Dialog>
        </AriaPopover>
      )}
    </DialogTrigger>
  );
}

/** The sheet's footer for a popover: the sheet takes a node, the popover's footer may want `close` — read off the
 *  trigger's state, as the sheet's own inside does. */
function SheetFooter({ render }: { render: (close: () => void) => ReactNode }) {
  const state = useContext(OverlayTriggerStateContext);
  return <>{render(() => state?.close())}</>;
}
